import * as PIXI from "pixi.js";
import { loadPlayerAssets, setupPlayer } from "../actors/player";
import {
  loadScaffold,
  setupScaffold,
  SetupScaffoldProps,
} from "../actors/scaffold";
import { screenHeight, screenWidth } from "../constants";
import { CollisionManager, Rect } from "../managers/collisionManager";
import { InputManager } from "../managers/inputManager";
import { LoadManager } from "../managers/loadManager";
import { ScrollManager } from "../managers/scrollManager";
import { TimeCountManager } from "../managers/timeCountManager";
import { SoundManager } from "../managers/soundManager";
import { setupStageUI } from "./ui";
import { loadHeartAssets, setupHeart, SetupHeartProps } from "../actors/heart";
import { HeartManager } from "../managers/heartManager";
import { loadLoliAssets, setupLoli } from "../actors/loli";
import { LoliManager } from "../managers/loliManager";
import { sleep } from "../utils/sleep";
import { setupMonologue } from "../actors/monologue";
import { MonologueManager } from "../managers/monologueManager";
import { loadCatAssets, setupCat, SetupCatProps } from "../actors/cat";

export type ItemProps = Readonly<{
  heartAssets: ReturnType<typeof loadHeartAssets>;
  scaffoldAssets: ReturnType<typeof loadScaffold>;
  catAssets: ReturnType<typeof loadCatAssets>;
  parent: PIXI.Container;
  ticker: PIXI.Ticker;
  collisionManager: CollisionManager;
  heartManager: HeartManager;
  soundManager: SoundManager;
}>;
export type Item = (props: ItemProps) => () => void;

export const scaffold = (
  props: Pick<SetupScaffoldProps, "x" | "y" | "width" | "height">,
): Item => (itemProps: ItemProps) => setupScaffold({ ...props, ...itemProps });

export const heart = (props: Pick<SetupHeartProps, "x" | "y">): Item => (
  itemProps: ItemProps,
) => setupHeart({ ...props, ...itemProps });

export const cat = (
  props: Pick<SetupCatProps, "startX" | "endX" | "y" | "intervalMs">,
): Item => (itemProps: ItemProps) => setupCat({ ...props, ...itemProps });

type StageState = "playing" | "timeouted" | "cleared";

type SetupStageProps = Readonly<{
  parent: PIXI.Container;
  ticker: PIXI.Ticker;
  soundManager: SoundManager;
  width: number;
  height: number;
  timeLimit: number;
  playerInitialPos: {
    readonly x: number;
    readonly y: number;
  };
  items: readonly Item[];
  loliPosition: {
    x: number;
    y: number;
  };
  goalRect: Rect;
  retry: () => void;
  backToTitle: () => void;
}>;
export function setupStage({
  parent,
  ticker,
  soundManager,
  width,
  height,
  timeLimit,
  playerInitialPos,
  items,
  loliPosition,
  goalRect,
  retry,
  backToTitle,
}: SetupStageProps) {
  const loadManager = new LoadManager();

  const stageContainer = new PIXI.Container();

  const playerAssets = loadPlayerAssets(loadManager);
  const scaffoldAssets = loadScaffold(loadManager);
  const heartAssets = loadHeartAssets(loadManager);
  const loliAssets = loadLoliAssets(loadManager);
  const catAssets = loadCatAssets(loadManager);

  const loliManager = new LoliManager();
  const monologueManager = new MonologueManager();

  let dispose: () => void = () => {};
  let stageState: StageState = "playing";

  loadManager.waitForLoad().then(() => {
    const timeCountManager = new TimeCountManager(ticker);
    const inputManager = new InputManager(ticker);
    const scrollManager = new ScrollManager(
      width,
      height,
      screenWidth,
      screenHeight,
      playerInitialPos.x,
      playerInitialPos.y,
    );
    const collisionManager = new CollisionManager(height);

    const heartManager = new HeartManager();

    soundManager.playChatBGM();

    const disposeUI = setupStageUI({
      heartAssets,
      parent,
      ticker,
      timeCountManager,
      heartManager,
    });
    const disposePlayer = setupPlayer({
      playerAssets,
      parent: stageContainer,
      ticker,
      soundManager,
      inputManager,
      collisionManager,
      scrollManager,
      ...playerInitialPos,
      stageWidth: width,
    });
    const disposeItems = items.map((item) =>
      item({
        parent: stageContainer,
        ticker,
        collisionManager,
        scaffoldAssets,
        heartAssets,
        catAssets,
        heartManager,
        soundManager,
      }),
    );

    const disposeLoli = setupLoli({
      loliAssets,
      parent: stageContainer,
      ticker,
      loliManager,
      ...loliPosition,
    });

    const timeout = async () => {
      inputManager.pause();
      await sleep(250);
      const dispose = setupMonologue({ parent, ticker, monologueManager });
      await sleep(250);
      monologueManager.text = "嘘……";
      await sleep(1500);
      monologueManager.text = "ハムスターさんなら来てくれると思ったのに……";
      await sleep(1500);
      dispose();
      soundManager.stopChatBGM();
      retry();
    };

    const gameClear = async () => {
      timeCountManager.stopCountDown();
      inputManager.pause();
      await sleep(250);
      let dispose = setupMonologue({ parent, ticker, monologueManager });
      await sleep(250);
      switch (heartManager.num) {
        case 3:
          monologueManager.text = "すごい…こんなに持ってきてくれたんだね！";
          break;
        default:
          monologueManager.text = "持ってきてくれたんだね！";
          break;
      }
      await sleep(2000);
      dispose();
      monologueManager.text = "";
      await sleep(750);
      loliManager.emotion = "smile";
      await sleep(1000);
      dispose = setupMonologue({ parent, ticker, monologueManager });
      await sleep(250);
      monologueManager.text = "ありがとう！！";
      await sleep(2000);
      dispose();
      soundManager.stopChatBGM();
      backToTitle();
    };

    const onTick = () => {
      stageContainer.x = scrollManager.containerX;
      stageContainer.y = scrollManager.containerY;

      if (timeCountManager.restMs === 0 && stageState === "playing") {
        stageState = "timeouted";
        timeout();
      }

      const collides = collisionManager.check(goalRect);
      if (collides.some((c) => c.type === "player")) {
        if (heartManager.num > 0 && stageState === "playing") {
          stageState = "cleared";
          gameClear();
        }
      }
    };

    timeCountManager.startCountDown(timeLimit);

    parent.addChild(stageContainer);
    ticker.add(onTick);
    dispose = () => {
      inputManager.dispose();
      timeCountManager.dispose();
      disposeUI();
      disposePlayer();
      disposeItems.forEach((d) => d());
      disposeLoli();
      parent.removeChild(stageContainer);
      ticker.remove(onTick);
    };
  });

  return () => dispose();
}
